import { useMemo } from 'react';
import {
  DomainsWithSourcesStatsWithShare,
  DomainStatsWithShare,
  SourceStatsWithShare,
} from './UseDomainsWithSourcesStatsShare';

export type DomainStatsWithComparison = DomainStatsWithShare & {
  previousShare: number;
  previousPromptCount: number;
  shareChange: number;
  promptCountChange: number;
  sources?: (SourceStatsWithShare & {
    previousShare: number;
    previousPromptCount: number;
    shareChange: number;
    promptCountChange: number;
  })[];
};

export type DomainsWithSourcesStatsWithComparison = {
  domains: DomainStatsWithComparison[];
  pagination?: DomainsWithSourcesStatsWithShare['pagination'];
  totalCount?: number;
};

/**
 * Hook to compare domains with sources stats between two periods
 * Calculates share changes and prompt count changes (absolute numeric difference) between current and previous period
 * Requires shares to be pre-calculated using useDomainsWithSourcesStatsShare
 * Returns only shareChange and promptCountChange, not share (share comes from share hooks)
 */
export const useDomainsWithSourcesStatsComparison = (
  currentDataWithShare: DomainsWithSourcesStatsWithShare | undefined,
  previousDataWithShare: DomainsWithSourcesStatsWithShare | undefined
): DomainsWithSourcesStatsWithComparison | undefined => {
  return useMemo(() => {
    if (!currentDataWithShare?.domains) {
      return currentDataWithShare
        ? {
            domains: [],
            pagination: currentDataWithShare.pagination,
            totalCount: currentDataWithShare.totalCount,
          }
        : undefined;
    }

    // Create maps for fast lookup of previous period data
    const previousDomainsMap = new Map(
      previousDataWithShare?.domains?.map((domain) => [domain.domain, domain]) || []
    );

    const previousSourcesMap = new Map<string, Map<string, SourceStatsWithShare>>();
    previousDataWithShare?.domains?.forEach((domain) => {
      const sourcesMap = new Map(
        (domain.sources as SourceStatsWithShare[])?.map((source) => [source.source, source]) || []
      );
      previousSourcesMap.set(domain.domain, sourcesMap);
    });

    // Process domains with share changes
    const enrichedDomains: DomainStatsWithComparison[] = currentDataWithShare.domains.map(
      (currentDomain) => {
        const currentShare = currentDomain.share;

        const previousDomain = previousDomainsMap.get(currentDomain.domain);
        const previousShare = previousDomain?.share || 0;

        const domainShareChange = currentShare - previousShare;

        // Calculate prompt count change for domain (absolute numeric difference)
        const currentDomainPromptCount = currentDomain.promptCount || 0;
        const previousDomainPromptCount = previousDomain?.promptCount || 0;
        const domainPromptCountChange = currentDomainPromptCount - previousDomainPromptCount;

        // Process sources within this domain
        const enrichedSources: (SourceStatsWithShare & {
          previousShare: number;
          previousPromptCount: number;
          shareChange: number;
          promptCountChange: number;
        })[] =
          (currentDomain.sources as SourceStatsWithShare[])?.map((currentSource) => {
            const currentSourceShare = currentSource.share;

            const previousSource = previousSourcesMap
              .get(currentDomain.domain)
              ?.get(currentSource.source);
            const previousSourceShare = previousSource?.share || 0;

            const sourceShareChange = currentSourceShare - previousSourceShare;

            // Calculate prompt count change for source (absolute numeric difference)
            const currentSourcePromptCount = currentSource.promptCount || 0;
            const previousSourcePromptCount = previousSource?.promptCount || 0;
            const sourcePromptCountChange = currentSourcePromptCount - previousSourcePromptCount;

            return {
              ...currentSource,
              previousShare: previousSourceShare,
              previousPromptCount: previousSourcePromptCount,
              shareChange: sourceShareChange,
              promptCountChange: sourcePromptCountChange,
            } as SourceStatsWithShare & {
              previousShare: number;
              previousPromptCount: number;
              shareChange: number;
              promptCountChange: number;
            };
          }) || [];

        return {
          ...currentDomain,
          previousShare,
          previousPromptCount: previousDomainPromptCount,
          shareChange: domainShareChange,
          promptCountChange: domainPromptCountChange,
          sources: enrichedSources,
        } as DomainStatsWithComparison;
      }
    );

    return {
      domains: enrichedDomains,
      pagination: currentDataWithShare.pagination,
      totalCount: currentDataWithShare.totalCount,
    };
  }, [currentDataWithShare, previousDataWithShare]);
};
